import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import FAQAccordion from "@/components/FAQAccordion";
import RelatedBlock from "@/components/RelatedBlock";
import ServiceCard from "@/components/cards/ServiceCard";
import RegionCard from "@/components/cards/RegionCard";
import { COMPANY } from "@/data/company";
import { ALL_SERVICES, REGIONS, TOWNS } from "@/data/nav";
import { trackPhoneClick } from "@/lib/track";

const RegionHubTemplate = ({ data }) => {
  const services = data.servicesFocus
    .map((slug) => ALL_SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);
  const siblingRegions = REGIONS.filter((r) => r.slug !== data.slug);
  const townsInRegion = TOWNS.filter((t) => t.region === data.slug);

  return (
    <div data-testid={`region-page-${data.slug}`}>
      <Seo
        title={data.seo.title}
        description={data.seo.description}
        path={`/coverage/${data.slug}`}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <PhotoBackdrop src={data.heroImage} position={data.heroPosition} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative">
          <p className="eyebrow">Coverage · {data.name}</p>
          <h1
            className="font-display mt-6 text-white max-w-4xl"
            style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1 }}
            data-testid="region-h1"
          >
            {data.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {data.subhead}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={COMPANY.phone.tel}
              onClick={() => trackPhoneClick(`region_${data.slug}_hero`)}
              className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="region-hero-call"
            >
              <Phone size={16} /> Call an engineer in {data.name}
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="region-hero-enquire"
            >
              Send an enquiry <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <Breadcrumb
        items={[
          { label: "Coverage", to: "/coverage" },
          { label: data.name },
        ]}
      />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="eyebrow">The {data.name} industrial base</p>
          <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
            Who we work with here.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[15.5px] leading-relaxed text-[color:var(--lt-text-dim)] whitespace-pre-line">
            {data.intro}
          </p>
        </div>
      </section>

      {/* SERVICES DELIVERED HERE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Services delivered in {data.name}</p>
        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
          Most-requested services in the region.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s) => (
            <ServiceCard key={s.slug} slug={s.slug} name={s.name} blurb={s.blurb} categoryName={s.categoryName} />
          ))}
        </div>
        <div className="mt-6">
          <Link to="/services" className="inline-flex items-center gap-2 text-[13.5px] text-[color:var(--lt-blue-bright)] font-semibold">
            View all services <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* RESPONSE PROMISE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="glass-card rounded-xl p-8 lg:p-10 max-w-4xl">
          <p className="eyebrow">Response promise</p>
          <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(24px, 2.8vw, 34px)", lineHeight: 1.05 }}>
            Honest arrival times, always.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">{data.responsePromise}</p>
        </div>
      </section>

      {/* PRESENCE (generic — no named estate claims) */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">On the ground</p>
        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
          Sites we cover in {data.name}.
        </h2>
        <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
          We support sites across industrial estates, manufacturing parks, logistics hubs and commercial premises throughout {data.name}.
        </p>
      </section>

      {/* TOWNS (if any published) */}
      {townsInRegion.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
          <p className="eyebrow">Towns in {data.name}</p>
          <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
            Deeper local coverage.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {townsInRegion.map((t) => (
              <RegionCard key={t.slug} slug={t.slug} name={t.name} kind="town" blurb={`Coverage detail for ${t.name}.`} />
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">{data.name} FAQ</p>
            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
              Common questions about local work.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={data.faq} testIdPrefix={`region-faq-${data.slug}`} />
          </div>
        </div>
      </section>

      {/* CTA BANNER — regional emergency flavour */}
      <CTABanner
        variant="emergency"
        title={`Breakdown in ${data.name}? Speak to an engineer in under 60 seconds.`}
        body={`24/7 rapid response for line-down mechanical, electrical and controls emergencies across ${data.name}.`}
        phoneLocation={`region_${data.slug}_bottom`}
      />

      {/* RELATED — other regions */}
      <RelatedBlock
        title="Also covered"
        groups={[
          {
            eyebrow: "Other regions",
            items: siblingRegions.map((r) => (
              <RegionCard key={r.slug} slug={r.slug} name={r.name} blurb={r.blurb} />
            )),
          },
        ]}
      />
    </div>
  );
};

export default RegionHubTemplate;
